import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import slugify from "slugify";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import { useTranslation } from "react-i18next";
import ImageUploader from "../../ImageUploader";
import RichTextEditor from "../../RichTextEditor";

const AddDepartment = ({ isSearchable }) => {
  const editor = useRef(null);
  const { t, i18n } = useTranslation([]);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [icon, setIcon] = useState(null); // File for upload
  const [profilePhoto, setProfilePhoto] = useState("https://placehold.co/100");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null); // Icon preview
  const [departmentData, setDepartmentData] = useState({
    translations: {
      en: { name: "", description: "", metaTitle: "", metaDescription: "" },
      bn: { name: "", description: "", metaTitle: "", metaDescription: "" },
    },
    slug: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-generate slug from English name
  useEffect(() => {
    if (departmentData.translations.en.name) {
      const generatedSlug = slugify(departmentData.translations.en.name.trim(), {
        lower: true,
        strict: true,
      });
      setDepartmentData((prev) => ({
        ...prev,
        slug: generatedSlug,
      }));
    }
  }, [departmentData.translations.en.name]);

  // Cleanup previewURL on component unmount
  useEffect(() => {
    return () => {
      if (previewURL) {
        URL.revokeObjectURL(previewURL);
      }
    };
  }, [previewURL]);

  // Language Switch (English <-> Bangla)
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

  const handleLanguageChange = (selectedOption) => {
    i18n.changeLanguage(selectedOption.value);
    setSelectedLanguage(selectedOption.value);
  };

  // Handle input field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setDepartmentData((prev) => {
      const updatedTranslations = {
        ...prev.translations,
        [selectedLanguage]: {
          ...prev.translations[selectedLanguage],
          [name]: value,
        },
      };
      if (selectedLanguage === "en" && name === "name") {
        const generatedSlug = slugify(value, { lower: true, strict: true });
        return { ...prev, translations: updatedTranslations, slug: generatedSlug };
      }
      return { ...prev, translations: updatedTranslations };
    });
  };

  // Handle manual slug changes
  const handleSlugChange = (e) => {
    const slugValue = e.target.value;
    const customSlug = slugValue.replace(/\s+/g, "");
    setDepartmentData((prev) => ({
      ...prev,
      slug: slugify(customSlug, { lower: true, strict: true }),
    }));
  };

  // Handle RichTextEditor content changes
  const handleContentChange = (newContent) => {
    setDepartmentData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [selectedLanguage]: {
          ...prev.translations[selectedLanguage],
          description: newContent,
        },
      },
    }));
  };

  // Handle image upload
  const handleImageUpload = (file) => {
    if (previewURL) {
      URL.revokeObjectURL(previewURL);
      setPreviewURL(null);
    }

    if (file instanceof File) {
      const fileURL = URL.createObjectURL(file);
      setProfilePhoto(fileURL);
      setPreviewURL(fileURL);
      setIcon(file);
      setSelectedFile(file);
    } else {
      setProfilePhoto("https://placehold.co/100");
      setIcon(null);
      setSelectedFile(null);
    }
  };

  // Reset all form fields, including image states
  const resetForm = () => {
    setDepartmentData({
      translations: {
        en: { name: "", description: "", metaTitle: "", metaDescription: "" },
        bn: { name: "", description: "", metaTitle: "", metaDescription: "" },
      },
      slug: "",
    });
    setIcon(null);
    setProfilePhoto("https://placehold.co/100");
    setSelectedFile(null);
    if (previewURL) {
      URL.revokeObjectURL(previewURL);
      setPreviewURL(null);
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("translations", JSON.stringify(departmentData.translations));
      formData.append("slug", departmentData.slug);
      if (icon) {
        formData.append("icon", icon);
      }

      const response = await axios.post(
        "https://api.muktihospital.com/api/department",
        formData,
        {
          headers: {
            "x-api-key":
              "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
              "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Department added successfully!");
        resetForm();
      } else {
        toast.error("Failed to add department.");
      }
    } catch (error) {
      console.error("Error adding department:", error);
      toast.error("An error occurred while adding the department.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentTranslation = departmentData.translations[i18n.language] || {
    content: "",
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Department"},
    { label: "Add Department" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" />
      <PageHeading title="Add Department" breadcrumbs={breadcrumbs} />
      {/* Language switcher */}
      <div className="mb-4 flex justify-end">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((option) => option.value === i18n.language)}
          isSearchable={isSearchable}
        />
      </div>
      <div>
        {/* Department form */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div>
              <div className="bg-white shadow-sm rounded-lg">
                <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    Basic Information
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
                  {/* Department Name */}
                  <div>
                    <label className="label">Department Name</label>
                    {isSubmitting ? (
                      <Skeleton height={35} />
                    ) : (
                      <input
                        type="text"
                        name="name"
                        placeholder="Enter Department Name"
                        className="input-field"
                        value={departmentData.translations[selectedLanguage].name}
                        onChange={handleChange}
                      />
                    )}
                  </div>

                  {/* Slug Field */}
                  <div>
                    <label className="label">Slug</label>
                    <input
                      type="text"
                      name="slug"
                      className="input-field"
                      placeholder="Enter Slug"
                      value={departmentData.slug}
                      onChange={handleSlugChange}
                    />
                  </div>
                </div>

                {/* Department Description */}
                <div className="p-5">
                  <label className="label">Department Description</label>
                  {isSubmitting ? (
                    <Skeleton height={35} />
                  ) : (
                    <RichTextEditor
                      value={currentTranslation.description}
                      onChange={handleContentChange}
                    />
                  )}
                </div>
              </div>
            </div>
            <div>
              <div className="bg-white shadow-sm rounded-lg">
                <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    SEO Configuration
                  </h2>
                </div>
                <div className="p-5 grid grid-cols-2 gap-6">
                  {/* Meta Title */}
                  <div>
                    <label className="label">Meta Title</label>
                    {isSubmitting ? (
                      <Skeleton height={35} />
                    ) : (
                      <input
                        type="text"
                        name="metaTitle"
                        placeholder="Enter Meta Title (SEO)"
                        className="input-field"
                        value={
                          departmentData.translations[selectedLanguage].metaTitle
                        }
                        onChange={handleChange}
                      />
                    )}
                  </div>

                  {/* Meta Description */}
                  <div>
                    <label className="label">Meta Description</label>
                    {isSubmitting ? (
                      <Skeleton height={50} />
                    ) : (
                      <input
                        name="metaDescription"
                        placeholder="Enter Meta Description (SEO)"
                        className="input-field"
                        value={
                          departmentData.translations[selectedLanguage]
                            .metaDescription
                        }
                        onChange={handleChange}
                      />
                    )}
                  </div>
                </div>
              </div>
              {/* Image Uploader */}
              <div className="bg-white shadow-sm rounded-lg mt-5">
                <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    Upload Profile Photo
                  </h2>
                </div>
                <div className="p-5">
                  <ImageUploader
                    onFileUpload={handleImageUpload}
                    reset={isSubmitting ? false : !icon && !previewURL}
                  />
                </div>
              </div>
              {/* Submit Button */}
              <div className="mt-6 flex gap-4 justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDepartment;

